/**
 * Button Component
 *
 * Enterprise-grade button with multiple variants, sizes, and states.
 * Features colorful gradients, hover effects, and smooth transitions.
 *
 * CRITICAL CONSTRAINTS:
 * - Tailwind CSS core utilities only (NO custom compilation)
 * - TypeScript strict mode (NO 'any' types)
 * - 200-300ms transition timing
 * - Professional shadows and hover effects
 */

import React from 'react';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

/**
 * Button Component
 *
 * @param variant - Button style variant (default: 'primary')
 * @param size - Button size (default: 'md')
 * @param onClick - Click handler
 * @param disabled - Disabled state
 * @param loading - Loading state (shows spinner)
 * @param children - Button content
 * @param type - HTML button type
 * @param className - Additional Tailwind classes
 */
export function Button({
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  loading = false,
  children,
  type = 'button',
  className = '',
}: ButtonProps): React.JSX.Element {
  // Base classes (applied to all buttons)
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-md shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  // Variant classes (color and style)
  const variantClasses: Record<string, string> = {
    primary:
      'text-white bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 hover:shadow-lg focus:ring-blue-500',
    secondary:
      'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:shadow-md focus:ring-blue-500',
    danger:
      'text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 hover:shadow-lg focus:ring-red-500',
    ghost:
      'text-gray-700 bg-transparent hover:bg-gray-100 hover:shadow-sm focus:ring-blue-500',
    outline:
      'text-blue-600 bg-transparent border-2 border-blue-600 hover:bg-blue-50 hover:shadow-md focus:ring-blue-500',
  };

  // Size classes (padding and text size)
  const sizeClasses: Record<string, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  // Combine classes
  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={buttonClasses}
      aria-busy={loading}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {loading ? 'Loading...' : children}
    </button>
  );
}
